/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {SensorUnits} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import {systemOfUnitsSelector} from '@/store/selectors/login-selectors';
import ThresholdAlert from './ThresholdAlert';
import {getTemperatureDisplayUnit} from './utils';

export interface ChartsDataType {
  eventTime: string;
  y: number;
  settings: {
    minTemperature?: number;
    maxTemperature?: number;
    minLightLevel?: number;
    maxLightLevel?: number;
    maxImpact?: number;
  }
  systemOfUnits?: {
    value: string;
  }
  units: string;
  threshold?: {
    max?: number;
    min?: number;
  };
}
export interface ChartsTooltipProps {
  point: {
    data: ChartsDataType
  }
}

const useStyles = makeStyles((theme: DLSTheme) => {
  const {text} = getAssetDetailsContentStyles();
  return {
    tooltipContainer : {
      display       : 'flex',
      flexDirection : 'column'
    },
    text : {
      font     : dlsTypography('body3'),
      color    : dlsColor('black'),
      fontSize : 12,
      padding  : '2px 0'
    },
    // maps the tooltip's value line to the shared `text` style ...
    currentText : {
      fontFamily    : text.fontFamily,
      fontWeight    : text.fontWeight,
      fontSize      : text.fontSize,
      lineHeight    : text.lineHeight,
      letterSpacing : text.letterSpacing,
      color         : text.color,
      padding       : 0
    }
  };
});

/**
 * ChartsTooltip is a component used for displaying current data being hovered over in a chart/graph.
 *
 * ```typescript
 *  import {ChartsTooltip} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <SomeComponent
 *     ...
 *     customToolTip={ChartsTooltip}/>
 * ```
 */
export const ChartsTooltip = ({point: {data}}: ChartsTooltipProps) => {
  const classes                = useStyles();
  const isCurrent              = isCurrentTheme();
  const systemOfUnits          = useSelector(systemOfUnitsSelector);
  const unit                   = data?.units;
  const celsius                = SensorUnits.C;
  const fahrenheit             = SensorUnits.F;
  const isTemperature          = unit === fahrenheit || unit === celsius;
  const temperatureDisplayUnit = getTemperatureDisplayUnit(unit as SensorUnits, systemOfUnits?.value);
  return (
    <div className={classes.tooltipContainer}>
      <ThresholdAlert data={data} unit={unit} />
      <span className={themedClassName(classes.text, classes.currentText, isCurrent)}>
        {data?.y}
        {isTemperature ? '°' : null}
        &nbsp;{isTemperature ? temperatureDisplayUnit : data?.units}
        &nbsp;{getFormattedDate(data?.eventTime)}
      </span>
    </div>
  );
};
